/**
 * The Preview panel (TaskBrief "Preview and test panel"; ADR 0020): the EXPORTED package (POST /export, unzipped
 * here) running in a sandboxed, opaque-origin frame at its authored resolution, scaled to fit. The frame runs the
 * shared frame host (packages/harness); package files go in as ArrayBuffers and become the frame's own blob: URLs
 * (v_assetUrls), the path PRISM's preview uses.
 *
 * - Real time: Load (Data panel values), Play, Next step, Go to step, Update, Stop, custom actions (with a payload
 *   editor when they have a schema), Dispose, skip animation.
 * - Timeline: a mock PRISM clip (length, step marks, updates, out point) mapped exactly like PRISM: load
 *   non-realtime → setActionsSchedule → goToTime(n·1000·den/num) per frame; scrubber, frame stepping and playback
 *   that seeks on every animation frame (coalesced, like PRISM's player).
 * - Log: every call with its payload, currentStep and timing, and console errors forwarded from the frame.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, RefreshCw, SkipBack, Trash2, TriangleAlert, X } from 'lucide-react';
import { PreviewFrame, type CallRecord, type LoadedPackage } from '@prism-graphics/harness/frame/client';
import { DEFAULT_HOLD_SECONDS, clipSchedule, defaultClip, manifestFrameRate, stepCountOf, stopFrame, timestamp, type Clip, type FrameRate } from '@prism-graphics/harness/clip';
import { formatTimecode, fps, type Scene } from '@prism-graphics/scene-schema';
import { api } from '../api.ts';
import { formatBytes, t } from '../i18n.ts';
import { onTablistKeyDown, tabProps } from '../ui/controls.tsx';
import { useDialogFocus } from '../panels/Dialogs.tsx';
import { assetCache } from '../state/assets.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { customActionsOf, isFailure, layoutsOfManifest, logRecord, payloadTemplate, previewData, short, statusOf, type LogEntry } from './model.ts';
import { readPackageZip, type PackageFiles } from './unzip.ts';

export type PreviewBackground = 'checker' | 'black' | 'reference';
type Mode = 'realtime' | 'timeline';

interface PackageInfo {
  loaded: LoadedPackage;
  pkg: PackageFiles;
  sizeBytes: number;
  limitBytes: number;
  thumbnail: 'rendered' | 'placeholder' | undefined;
}

let logKey = 1_000_000;

/** The size of layout `id` in a package (undefined: the package's first, the default). */
function sizeIn(pkg: PackageFiles, id: string): { width: number; height: number } | undefined {
  const bytes = pkg.files.get(pkg.manifestPath);
  if (!bytes) return undefined;
  const l = layoutsOfManifest(JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>).find((x) => x.id === id);
  return l ? { width: l.width, height: l.height } : undefined;
}

export function PreviewPanel({ store, limitBytes, onClose }: { store: EditorStore; limitBytes: number | undefined; onClose: () => void }) {
  const scene = useEditor(store, sceneView);
  const testData = useEditor(store, (s) => s.testData);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const frameRef = useRef<PreviewFrame | null>(null);
  const [info, setInfo] = useState<PackageInfo>();
  const [busy, setBusy] = useState<string | undefined>(t('preview.exporting'));
  const [error, setError] = useState<string>();
  const [mode, setMode] = useState<Mode>('realtime');
  const [log, setLog] = useState<LogEntry[]>([]);
  const [skip, setSkip] = useState(false);
  const [bg, setBg] = useState<PreviewBackground>('checker');
  const [bgImage, setBgImage] = useState<string>();
  const [zoom, setZoom] = useState<'fit' | 'actual'>('fit');
  // The layout to load (ADR 0037): the graphic element gets its size and renderCharacteristics its resolution, so
  // the runtime's own choice picks it. Starts at the layout being edited.
  const [layoutId, setLayoutId] = useState(store.layout);
  const layoutRef = useRef(layoutId);
  layoutRef.current = layoutId;
  const rootRef = useRef<HTMLDivElement>(null);
  const trapFocus = useDialogFocus(rootRef, { focusFirst: false });

  const manifest = info?.loaded.manifest;
  const pkgScene = info?.loaded.scene as unknown as Scene | undefined;
  const rate: FrameRate = useMemo(() => (manifest ? manifestFrameRate(manifest) : scene.canvas.frameRate), [manifest, scene.canvas.frameRate]);
  const data = useCallback(() => (manifest ? previewData(manifest, pkgScene ?? scene, testData) : {}), [manifest, pkgScene, scene, testData]);

  const addLog = useCallback((e: { kind: 'console'; text: string; level: 'error' | 'warn' } | { kind: 'info'; text: string }) => setLog((l) => [...l, { ...e, key: logKey++ } as LogEntry].slice(-400)), []);

  /** Export the saved draft and mount it in the frame (a fresh element). */
  const exportAndMount = useCallback(async () => {
    const frame = frameRef.current;
    if (!frame) return;
    setBusy(t('preview.exporting'));
    setError(undefined);
    try {
      await store.saveNow();
      const exp = await api.exportDraft(store.state.draftId);
      const pkg = await readPackageZip(new Uint8Array(await exp.blob.arrayBuffer()));
      const loaded = await frame.loadFiles(pkg.files, pkg.manifestPath, sizeIn(pkg, layoutRef.current));
      setInfo({ loaded, pkg, sizeBytes: exp.sizeBytes, limitBytes: exp.sizeLimitBytes ?? limitBytes ?? 5 * 1024 * 1024, thumbnail: exp.thumbnail });
      addLog({ kind: 'info', text: t('preview.loadedPackage', { id: pkg.id, size: formatBytes(exp.sizeBytes), files: pkg.files.size }) });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(undefined);
    }
  }, [store, limitBytes, addLog]);

  // The frame and its messages; then the first export (deferred a task, so StrictMode's double effect exports once).
  useEffect(() => {
    const iframe = iframeRef.current!;
    const frame = new PreviewFrame(iframe, {
      onRecord: (r) => setLog((l) => logRecord(l, r)),
      onConsole: (text, level) => addLog({ kind: 'console', text, level }),
    });
    frameRef.current = frame;
    const timer = setTimeout(() => void exportAndMount(), 0);
    return () => {
      clearTimeout(timer);
      frame.dispose();
      frameRef.current = null;
    };
  }, [addLog, exportAndMount]);

  /** A fresh graphic element from the package (a renderer creates one per load). */
  const remount = useCallback(async () => {
    const frame = frameRef.current;
    if (!frame || !info) throw new Error('no package');
    return frame.loadFiles(info.pkg.files, info.pkg.manifestPath, sizeIn(info.pkg, layoutId));
  }, [info, layoutId]);

  /** Load the package at another layout's size (a fresh element). */
  const chooseLayout = useCallback(
    async (id: string) => {
      setLayoutId(id);
      const frame = frameRef.current;
      if (!frame || !info) return;
      setBusy(t('preview.loading'));
      try {
        const loaded = await frame.loadFiles(info.pkg.files, info.pkg.manifestPath, sizeIn(info.pkg, id));
        setInfo({ ...info, loaded });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(undefined);
      }
    },
    [info],
  );
  const packageLayouts = manifest ? layoutsOfManifest(manifest) : [];

  const call = useCallback((method: string, params?: unknown): Promise<CallRecord> => {
    const frame = frameRef.current;
    if (!frame) return Promise.reject(new Error('no frame'));
    return frame.call(method, params);
  }, []);

  const renderCharacteristics = useMemo(
    () => (info ? { resolution: { width: info.loaded.width, height: info.loaded.height }, frameRate: fps(rate), accessToPublicInternet: false, v_frameRate: rate } : undefined),
    [info, rate],
  );
  const withSkip = (p: Record<string, unknown> = {}) => (skip ? { ...p, skipAnimation: true } : p);

  const doLoadRealtime = async () => {
    await remount();
    await call('load', { data: data(), renderType: 'realtime', renderCharacteristics });
  };

  // ------------------------------------------------------------------------------------------------- timeline mode
  const [clip, setClip] = useState<Clip>();
  const [frameNo, setFrameNo] = useState(0);
  const [playing, setPlaying] = useState(false);
  const seekState = useRef<{ inFlight: boolean; pending: number | undefined; ready: boolean }>({ inFlight: false, pending: undefined, ready: false });
  const playRef = useRef<{ raf: number; t0: number; from: number }>({ raf: 0, t0: 0, from: 0 });

  useEffect(() => {
    if (manifest) setClip(defaultClip(manifest, manifestFrameRate(manifest), DEFAULT_HOLD_SECONDS));
  }, [manifest]);

  /** goToTime(timestamp(n)), coalesced: while one is in flight only the latest request is kept. */
  const seek = useCallback(
    (n: number) => {
      const s = seekState.current;
      if (!s.ready) return;
      if (s.inFlight) {
        s.pending = n;
        return;
      }
      s.inFlight = true;
      void call('goToTime', { timestamp: timestamp(n, rate) }).finally(() => {
        s.inFlight = false;
        const next = s.pending;
        s.pending = undefined;
        if (next !== undefined) seek(next);
      });
    },
    [call, rate],
  );

  const applyTimeline = useCallback(async () => {
    if (!manifest || !clip) return;
    seekState.current.ready = false;
    await remount();
    const d = data();
    await call('load', { data: d, renderType: 'non-realtime', renderCharacteristics });
    await call('setActionsSchedule', { schedule: clipSchedule(manifest, clip, rate, d) });
    seekState.current.ready = true;
    seek(Math.min(frameNo, clip.frames - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, clip, rate, remount, call, data, renderCharacteristics, seek]);

  useEffect(() => {
    if (mode === 'timeline' && info && clip) void applyTimeline();
    // Re-apply when the clip or the data changes (not on every frame).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, info, clip, testData]);

  const go = useCallback(
    (n: number) => {
      if (!clip) return;
      const f = Math.max(0, Math.min(clip.frames - 1, Math.round(n)));
      setFrameNo(f);
      seek(f);
    },
    [clip, seek],
  );

  const stopPlaying = useCallback(() => {
    cancelAnimationFrame(playRef.current.raf);
    playRef.current.raf = 0;
    setPlaying(false);
  }, []);

  const startPlaying = useCallback(() => {
    if (!clip) return;
    const from = frameNo >= clip.frames - 1 ? 0 : frameNo;
    playRef.current = { raf: 0, t0: performance.now(), from };
    setPlaying(true);
    const tick = () => {
      const p = playRef.current;
      // The frame under the playhead: floor of elapsed time at the authored rate (never accumulated).
      const n = p.from + Math.floor(((performance.now() - p.t0) * rate.num) / (1000 * rate.den));
      if (n >= clip.frames - 1) {
        go(clip.frames - 1);
        stopPlaying();
        return;
      }
      go(n);
      p.raf = requestAnimationFrame(tick);
    };
    playRef.current.raf = requestAnimationFrame(tick);
  }, [clip, frameNo, rate, go, stopPlaying]);

  useEffect(() => () => cancelAnimationFrame(playRef.current.raf), []);
  useEffect(() => {
    if (mode !== 'timeline') stopPlaying();
  }, [mode, stopPlaying]);

  // ------------------------------------------------------------------------------------------------- stage size
  const stageRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 800, height: 450 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ width: el.clientWidth, height: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const W = info?.loaded.width ?? scene.canvas.width;
  const H = info?.loaded.height ?? scene.canvas.height;
  const scale = zoom === 'actual' ? 1 : Math.max(0.05, Math.min((box.width - 32) / W, (box.height - 32) / H));

  // Reference still: the scene's editor background (colour or image) or an uploaded image.
  const ref = scene.canvas.background;
  const refUrl = bgImage ?? (ref?.asset ? assetCache.imageUrl(ref.asset) : undefined);
  const bgStyle = bg === 'black' ? { background: '#000' } : bg === 'reference' ? { background: ref?.color ?? '#000' } : undefined;
  useEffect(() => () => {
    if (bgImage) URL.revokeObjectURL(bgImage);
  }, [bgImage]);

  const over = info ? info.sizeBytes > info.limitBytes : false;
  const steps = manifest ? stepCountOf(manifest) : 1;
  const customActions = manifest ? customActionsOf(manifest) : [];
  const ready = Boolean(info) && !busy;

  return (
    <div
      className="preview-overlay"
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-title"
      data-testid="preview-panel"
      data-ready={ready ? 'true' : 'false'}
      onKeyDown={(e) => {
        // The editor's shortcuts must not act on the scene behind the preview.
        e.stopPropagation();
        if (e.key === 'Escape') onClose();
        trapFocus(e);
      }}
    >
      <header className="preview-head">
        <h2 id="preview-title">{t('preview.title')}</h2>
        <span className="preview-name">{scene.meta.name}</span>
        {info && (
          <span className={`preview-size${over ? ' over' : ''}`} data-testid="preview-size" data-bytes={info.sizeBytes} title={t('preview.sizeHint', { unpacked: formatBytes(info.pkg.unpackedBytes) })}>
            {over && <TriangleAlert size={14} aria-hidden />}
            {t('preview.size', { size: formatBytes(info.sizeBytes), limit: formatBytes(info.limitBytes) })}
            {over && (
              <span role="alert" className="sr-only">
                {t('preview.sizeOver')}
              </span>
            )}
          </span>
        )}
        <div className="spacer" />
        <div className="tabs preview-modes" role="tablist" aria-label={t('preview.mode')} onKeyDown={onTablistKeyDown}>
          {(['realtime', 'timeline'] as const).map((m) => (
            <button key={m} type="button" {...tabProps(mode === m, 'preview-body')} id={`preview-mode-${m}`} className={mode === m ? 'active' : ''} onClick={() => setMode(m)} data-testid={`preview-mode-${m}`}>
              {t(`preview.mode.${m}`)}
            </button>
          ))}
        </div>
        <label className="top-select">
          <span className="sr-only">{t('preview.background')}</span>
          <select value={bg} onChange={(e) => setBg(e.target.value as PreviewBackground)} data-testid="preview-background" aria-label={t('preview.background')}>
            <option value="checker">{t('preview.bg.checker')}</option>
            <option value="black">{t('preview.bg.black')}</option>
            <option value="reference">{t('preview.bg.reference')}</option>
          </select>
        </label>
        {bg === 'reference' && (
          <label className="btn small" title={t('preview.bg.uploadHint')}>
            {t('preview.bg.upload')}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              data-testid="preview-background-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setBgImage(URL.createObjectURL(f));
              }}
            />
          </label>
        )}
        {packageLayouts.length > 1 && (
          <label className="top-select" title={t('layouts.previewHint')}>
            <span className="sr-only">{t('layouts.preview')}</span>
            <select value={packageLayouts.some((l) => l.id === layoutId) ? layoutId : packageLayouts[0]!.id} onChange={(e) => void chooseLayout(e.target.value)} data-testid="preview-layout" aria-label={t('layouts.preview')}>
              {packageLayouts.map((l) => (
                <option key={l.id} value={l.id}>
                  {t('layouts.option', { width: l.width, height: l.height, label: l.label })}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="top-select">
          <span className="sr-only">{t('preview.zoom')}</span>
          <select value={zoom} onChange={(e) => setZoom(e.target.value as 'fit' | 'actual')} data-testid="preview-zoom" aria-label={t('preview.zoom')}>
            <option value="fit">{t('preview.zoom.fit')}</option>
            <option value="actual">{t('preview.zoom.actual')}</option>
          </select>
        </label>
        <button type="button" className="btn" onClick={() => void exportAndMount()} disabled={Boolean(busy)} data-testid="preview-reexport" title={t('preview.reexportHint')}>
          <RefreshCw size={14} aria-hidden /> {t('preview.reexport')}
        </button>
        <button type="button" className="icon-btn" aria-label={t('dialog.close')} onClick={onClose} data-testid="preview-close">
          <X size={16} />
        </button>
      </header>

      <div className="preview-body" id="preview-body" role="tabpanel" aria-labelledby={`preview-mode-${mode}`}>
        <div className={`preview-stage-area${zoom === 'actual' ? ' actual' : ''}`} ref={stageRef}>
          <div className={`preview-viewport${bg === 'checker' ? ' checker' : ''}`} style={{ width: W * scale, height: H * scale, ...bgStyle }} data-testid="preview-viewport">
            {bg === 'reference' && refUrl && <img className="preview-reference" src={refUrl} alt="" draggable={false} />}
            <iframe
              ref={iframeRef}
              className="preview-frame"
              title={t('preview.frameTitle')}
              src="/preview-frame.html"
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              data-testid="preview-frame"
              style={{ width: W, height: H, transform: `scale(${scale})` }}
            />
          </div>
          {busy && <div className="preview-busy">{busy}</div>}
          {error && (
            <div className="preview-error" role="alert" data-testid="preview-error">
              {error}
            </div>
          )}
        </div>

        <aside className="preview-side">
          {mode === 'realtime' ? (
            <section className="preview-controls" aria-label={t('preview.mode.realtime')}>
              <div className="btn-grid">
                <button type="button" className="btn" disabled={!ready} onClick={() => void doLoadRealtime()} data-testid="pv-load">
                  {t('preview.load')}
                </button>
                <button type="button" className="btn" disabled={!ready} onClick={() => void call('playAction', withSkip())} data-testid="pv-play">
                  {t('preview.play')}
                </button>
                <button type="button" className="btn" disabled={!ready} onClick={() => void call('playAction', withSkip({ delta: 1 }))} data-testid="pv-next">
                  {t('preview.next')}
                </button>
                <GoToStep steps={steps} disabled={!ready} onGo={(n) => void call('playAction', withSkip({ goto: n }))} />
                <button type="button" className="btn" disabled={!ready} onClick={() => void call('updateAction', withSkip({ data: data() }))} data-testid="pv-update">
                  {t('preview.update')}
                </button>
                <button type="button" className="btn" disabled={!ready} onClick={() => void call('stopAction', withSkip())} data-testid="pv-stop">
                  {t('preview.stop')}
                </button>
                <button type="button" className="btn danger" disabled={!ready} onClick={() => void call('dispose', {})} data-testid="pv-dispose">
                  {t('preview.dispose')}
                </button>
              </div>
              <label className="toggle-row">
                <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} data-testid="pv-skip" /> {t('preview.skip')}
              </label>
              {customActions.length > 0 && (
                <div className="preview-custom">
                  <h3>{t('preview.customActions')}</h3>
                  {customActions.map((a) => (
                    <CustomAction key={a.id} action={a} disabled={!ready} onRun={(payload) => void call('customAction', withSkip({ id: a.id, payload }))} />
                  ))}
                </div>
              )}
              <p className="hint">{t('preview.dataHint')}</p>
            </section>
          ) : (
            clip &&
            manifest && (
              <ClipEditor
                clip={clip}
                rate={rate}
                manifest={manifest}
                frameNo={frameNo}
                onChange={(c) => {
                  setClip(c);
                  if (frameNo >= c.frames) setFrameNo(c.frames - 1);
                }}
                updateData={() => {
                  const d = data();
                  const changed: Record<string, unknown> = {};
                  const defaults = previewData(manifest, pkgScene ?? scene, {});
                  for (const [k, v] of Object.entries(d)) if (JSON.stringify(v) !== JSON.stringify(defaults[k])) changed[k] = v;
                  return Object.keys(changed).length ? changed : d;
                }}
              />
            )
          )}
          <CallLog log={log} onClear={() => setLog([])} />
        </aside>
      </div>

      {mode === 'timeline' && clip && manifest && (
        <TimelineBar
          clip={clip}
          rate={rate}
          stop={stopFrame(manifest, clip, rate)}
          frameNo={frameNo}
          playing={playing}
          disabled={!ready}
          onGo={go}
          onPlay={() => (playing ? stopPlaying() : startPlaying())}
        />
      )}
    </div>
  );
}

function GoToStep({ steps, disabled, onGo }: { steps: number; disabled: boolean; onGo: (n: number) => void }) {
  const [n, setN] = useState(0);
  return (
    <div className="go-to-step">
      <button type="button" className="btn" disabled={disabled || steps === 0} onClick={() => onGo(n)} data-testid="pv-goto">
        {t('preview.goto')}
      </button>
      <select value={n} onChange={(e) => setN(Number(e.target.value))} disabled={disabled || steps === 0} aria-label={t('preview.gotoStep')} data-testid="pv-goto-step">
        {Array.from({ length: Math.max(1, steps) }, (_, i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </select>
    </div>
  );
}

function CustomAction({ action, disabled, onRun }: { action: ReturnType<typeof customActionsOf>[number]; disabled: boolean; onRun: (payload: unknown) => void }) {
  const [text, setText] = useState(() => JSON.stringify(payloadTemplate(action.schema), null, 1));
  const [bad, setBad] = useState(false);
  return (
    <div className="custom-action" data-testid={`pv-custom-${action.id}`}>
      <button
        type="button"
        className="btn"
        disabled={disabled || bad}
        title={action.description ?? ''}
        onClick={() => {
          try {
            onRun(action.schema ? JSON.parse(text || 'null') : null);
          } catch {
            setBad(true);
          }
        }}
        data-testid={`pv-custom-run-${action.id}`}
      >
        {action.name}
      </button>
      {action.schema && (
        <textarea
          aria-label={t('preview.payload', { name: action.name })}
          className={bad ? 'invalid' : ''}
          value={text}
          spellCheck={false}
          rows={2}
          data-testid={`pv-custom-payload-${action.id}`}
          onChange={(e) => {
            setText(e.target.value);
            try {
              JSON.parse(e.target.value || 'null');
              setBad(false);
            } catch {
              setBad(true);
            }
          }}
        />
      )}
    </div>
  );
}

function FrameInput({ label, value, max, onChange, testId }: { label: string; value: number; max: number; onChange: (n: number) => void; testId?: string }) {
  return (
    <label className="frame-input">
      <span className="sr-only">{label}</span>
      <input type="number" min={0} max={max} step={1} value={value} aria-label={label} data-testid={testId} onChange={(e) => onChange(Math.max(0, Math.min(max, Math.round(Number(e.target.value) || 0))))} />
    </label>
  );
}

function ClipEditor({ clip, rate, manifest, frameNo, onChange, updateData }: { clip: Clip; rate: FrameRate; manifest: Record<string, unknown>; frameNo: number; onChange: (c: Clip) => void; updateData: () => Record<string, unknown> }) {
  const sting = stepCountOf(manifest) === 0;
  const stop = stopFrame(manifest, clip, rate);
  const sec = (n: number) => (timestamp(n, rate) / 1000).toFixed(3);
  return (
    <section className="preview-controls clip-editor" aria-label={t('preview.clip')}>
      <div className="clip-row">
        <span>{t('preview.clipLength')}</span>
        <FrameInput label={t('preview.clipLength')} value={clip.frames} max={100_000} testId="clip-frames" onChange={(n) => onChange({ ...clip, frames: Math.max(1, n) })} />
        <span className="muted">{t('preview.framesSeconds', { s: sec(clip.frames) })}</span>
        <button type="button" className="btn small ghost" onClick={() => onChange(defaultClip(manifest, rate))} data-testid="clip-default" title={t('preview.clipDefaultHint')}>
          {t('preview.clipDefault')}
        </button>
      </div>
      {sting && <p className="hint">{t('preview.stingLength')}</p>}
      <div className="clip-row">
        <span>{t('preview.outPoint')}</span>
        <span className="mono" data-testid="clip-out">{stop === undefined ? t('preview.noStop') : t('preview.atFrame', { n: stop, s: sec(stop) })}</span>
      </div>
      <h3>
        {t('preview.stepMarks')}
        <button type="button" className="btn small" disabled={sting} onClick={() => onChange({ ...clip, stepMarks: [...clip.stepMarks, frameNo].sort((a, b) => a - b) })} data-testid="clip-add-step">
          {t('preview.addAtPlayhead')}
        </button>
      </h3>
      <ul className="mark-list">
        {clip.stepMarks.map((m, i) => (
          <li key={i} data-testid="clip-step-mark">
            <FrameInput label={t('preview.stepMark', { n: i + 1 })} value={m} max={clip.frames - 1} onChange={(n) => onChange({ ...clip, stepMarks: clip.stepMarks.map((x, j) => (j === i ? n : x)).sort((a, b) => a - b) })} />
            <span className="muted">{sec(m)} s</span>
            <button type="button" className="icon-btn" aria-label={t('preview.remove')} onClick={() => onChange({ ...clip, stepMarks: clip.stepMarks.filter((_, j) => j !== i) })}>
              <Trash2 size={13} />
            </button>
          </li>
        ))}
        {clip.stepMarks.length === 0 && <li className="muted">{t('preview.none')}</li>}
      </ul>
      <h3>
        {t('preview.updates')}
        <button type="button" className="btn small" onClick={() => onChange({ ...clip, updates: [...clip.updates, { frame: frameNo, data: updateData() }].sort((a, b) => a.frame - b.frame) })} data-testid="clip-add-update" title={t('preview.addUpdateHint')}>
          {t('preview.addAtPlayhead')}
        </button>
      </h3>
      <ul className="mark-list">
        {clip.updates.map((u, i) => (
          <li key={i} data-testid="clip-update-mark">
            <FrameInput label={t('preview.updateMark', { n: i + 1 })} value={u.frame} max={clip.frames - 1} onChange={(n) => onChange({ ...clip, updates: clip.updates.map((x, j) => (j === i ? { ...x, frame: n } : x)) })} />
            <span className="muted mono" title={short(u.data, 2000)}>
              {short(u.data, 40)}
            </span>
            <button type="button" className="icon-btn" aria-label={t('preview.remove')} onClick={() => onChange({ ...clip, updates: clip.updates.filter((_, j) => j !== i) })}>
              <Trash2 size={13} />
            </button>
          </li>
        ))}
        {clip.updates.length === 0 && <li className="muted">{t('preview.none')}</li>}
      </ul>
      <p className="hint">{t('preview.mappingHint')}</p>
    </section>
  );
}

function TimelineBar({ clip, rate, stop, frameNo, playing, disabled, onGo, onPlay }: { clip: Clip; rate: FrameRate; stop: number | undefined; frameNo: number; playing: boolean; disabled: boolean; onGo: (n: number) => void; onPlay: () => void }) {
  const pct = (n: number) => `${(n / Math.max(1, clip.frames - 1)) * 100}%`;
  return (
    <footer className="preview-timeline" data-testid="preview-timeline">
      <div className="transport">
        <button type="button" className="icon-btn" aria-label={t('preview.toStart')} disabled={disabled} onClick={() => onGo(0)} data-testid="tl-start">
          <SkipBack size={15} />
        </button>
        <button type="button" className="icon-btn" aria-label={t('preview.prevFrame')} disabled={disabled} onClick={() => onGo(frameNo - 1)} data-testid="tl-prev">
          <ChevronLeft size={15} />
        </button>
        <button type="button" className="icon-btn" aria-label={playing ? t('preview.pause') : t('preview.playTimeline')} disabled={disabled} onClick={onPlay} data-testid="tl-play" aria-pressed={playing}>
          {playing ? <Pause size={15} /> : <Play size={15} />}
        </button>
        <button type="button" className="icon-btn" aria-label={t('preview.nextFrame')} disabled={disabled} onClick={() => onGo(frameNo + 1)} data-testid="tl-next">
          <ChevronRight size={15} />
        </button>
      </div>
      <div className="scrub">
        <div className="marks" aria-hidden>
          {clip.stepMarks.map((m, i) => (
            <span key={`s${i}`} className="mark step" style={{ left: pct(m) }} title={t('preview.stepMark', { n: i + 1 })} />
          ))}
          {clip.updates.map((u, i) => (
            <span key={`u${i}`} className="mark update" style={{ left: pct(u.frame) }} title={t('preview.updateMark', { n: i + 1 })} />
          ))}
          {stop !== undefined && <span className="mark out" style={{ left: pct(stop) }} title={t('preview.outPoint')} />}
        </div>
        <input
          type="range"
          min={0}
          max={clip.frames - 1}
          step={1}
          value={frameNo}
          disabled={disabled}
          aria-label={t('preview.scrubber')}
          data-testid="tl-scrub"
          onChange={(e) => onGo(Number(e.target.value))}
        />
      </div>
      <div className="tc mono" data-testid="tl-time" data-frame={frameNo} data-timestamp={timestamp(frameNo, rate)}>
        <Tc label={t('preview.frame')}>{`${frameNo} / ${clip.frames - 1}`}</Tc>
        <Tc label={t('preview.timecode')}>{formatTimecode(frameNo, rate)}</Tc>
        <Tc label="ms">{timestamp(frameNo, rate).toFixed(3)}</Tc>
        <Tc label="fps">{`${rate.num}/${rate.den}`}</Tc>
      </div>
    </footer>
  );
}

function Tc({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="tc-item">
      <span className="muted">{label}</span> {children}
    </span>
  );
}

function CallLog({ log, onClear }: { log: LogEntry[]; onClear: () => void }) {
  const ref = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);
  return (
    <section className="preview-log" aria-label={t('preview.log')}>
      <h3>
        {t('preview.log')}
        <button type="button" className="btn small ghost" onClick={onClear} data-testid="pv-log-clear">
          {t('preview.clearLog')}
        </button>
      </h3>
      <ol ref={ref} data-testid="pv-log" aria-live="polite">
        {log.map((e) => {
          if (e.kind === 'info') return <li key={e.key} className="info">{e.text}</li>;
          if (e.kind === 'console')
            return (
              <li key={e.key} className="err" data-testid="pv-log-console">
                {e.text}
              </li>
            );
          const r = e.record;
          const status = statusOf(r);
          return (
            <li
              key={e.key}
              className={`call${isFailure(r) ? ' err' : ''}${r.settled ? '' : ' pending'}`}
              data-testid="pv-log-call"
              data-method={r.method}
              data-status={status ?? ''}
              data-step={r.currentStep ?? ''}
              data-settled={r.settled ? 'true' : 'false'}
              data-timestamp={(r.params as { timestamp?: number } | undefined)?.timestamp ?? ''}
              data-skip={(r.params as { skipAnimation?: boolean } | undefined)?.skipAnimation ? 'true' : 'false'}
              title={`${r.method}(${short(r.params, 4000)})${r.settled ? ` → ${r.error ? `threw ${r.error}` : short(r.result, 4000)}` : ''}`}
            >
              <span className="m">{r.method}</span>
              {e.repeat > 1 && <span className="muted"> ×{e.repeat}</span>}
              <span className="p"> {short(r.params, 90)}</span>
              <span className="r">
                {' → '}
                {r.error ? `threw ${r.error}` : r.settled ? short(r.result, 140) : '…'}
              </span>
              {r.currentStep !== undefined && <span className="s"> {t('preview.log.step', { step: r.currentStep })}</span>}
              {r.durationMs !== undefined && <span className="ms"> {r.durationMs.toFixed(1)} ms</span>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
